import { useState, useRef, useEffect } from 'react'
import type { GetStaticProps } from "next";
import QRCodeStyling from 'qr-code-styling'
import { useTranslation } from "next-i18next";
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { Card, CardContent } from "@/components/ui/card"
import Templates, { templates } from './_mods/templates'

type Props = {
}

export const getStaticProps: GetStaticProps<Props> = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale ?? "en", [
      "common",
      "description",
      "title",
      "keywords",
      "qrcode"
    ]))
  }
});


export default function Component() {
  const { t } = useTranslation("qrcode");

  const [url, setUrl] = useState('https://douni.one')
  const [size, setSize] = useState(200)
  const [errorLevel, setErrorLevel] = useState('H')
  const [backgroundColor, setBackgroundColor] = useState('#ffffff')
  const [dotColor, setDotColor] = useState('#000000')
  const [cornerSquareColor, setCornerSquareColor] = useState('#9333ea')
  const [cornerDotColor, setCornerDotColor] = useState('#9333ea')
  const [dotShape, setDotShape] = useState('square')
  const [cornerSquareShape, setCornerSquareShape] = useState('extra-rounded')
  const [cornerDotShape, setCornerDotShape] = useState('square')
  const [margin, setMargin] = useState(12)
  const [imageMargin, setImageMargin] = useState(3)
  const [logoImage, setLogoImage] = useState<File | null>(null)

  const qrRef = useRef<HTMLDivElement>(null)
  const qrCode = useRef<any>(null)

  useEffect(() => {
    const options: any = {
      width: size,
      height: size,
      data: url,
      margin: margin,
      type: 'svg',
      image: logoImage ? URL.createObjectURL(logoImage) : undefined,
      qrOptions: {
        errorCorrectionLevel: errorLevel
      },
      imageOptions: {
        crossOrigin: "anonymous",
        hideBackgroundDots: true,
        imageSize: 0.4,
        margin: imageMargin
      },
      dotsOptions: {
        type: dotShape,
        color: dotColor
      },
      backgroundOptions: {
        color: backgroundColor
      },
      cornersSquareOptions: {
        type: cornerSquareShape,
        color: cornerSquareColor
      },
      cornersDotOptions: {
        type: cornerDotShape,
        color: cornerDotColor
      }
    }

    qrCode.current = new QRCodeStyling(options)

    if (qrRef.current) {
      qrRef.current.innerHTML = ''
      qrCode.current.append(qrRef.current)
    }
  }, [url, size, errorLevel, backgroundColor, dotColor, cornerSquareColor,
    cornerDotColor, dotShape, cornerSquareShape, cornerDotShape, margin,
    imageMargin, logoImage])

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setLogoImage(file)
    }
  }

  const clearLogo = () => {
    setLogoImage(null)
  }

  const downloadQR = (format: 'png' | 'jpeg' | 'webp' | 'svg') => {
    qrCode.current?.download({
      extension: format
    })
  }

  const applyTemplate = (template: typeof templates[0]) => {
    setBackgroundColor(template.backgroundColor)
    setDotColor(template.dotColor)
    setCornerSquareColor(template.cornerSquareColor)
    setCornerDotColor(template.cornerDotColor)
    setDotShape(template.dotShape)
    setCornerSquareShape(template.cornerSquareShape)
    setCornerDotShape(template.cornerDotShape)
    // 点击之后需要滚动到二维码生成区域
    qrRef.current?.scrollIntoView({ behavior: 'smooth' })
  }



  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-purple-600 mb-4">{t("title")}</h1>
        <p className="text-xl text-gray-600">
          {t("description")}
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        <Card>
          <CardContent className="p-6">
            <div className="space-y-6">
              <div>
                <Label htmlFor="url">{t("form.url")}</Label>
                <Input
                  id="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder={t("form.placeholder.url")}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="errorLevel">{t("form.errorLevel")}</Label>
                  <Select value={errorLevel} onValueChange={setErrorLevel}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="L">Low</SelectItem>
                      <SelectItem value="M">Medium</SelectItem>
                      <SelectItem value="Q">Quartile</SelectItem>
                      <SelectItem value="H">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="size">{t("form.downloadSize")}</Label>
                  <Input
                    id="size"
                    type="number"
                    value={size}
                    onChange={(e) => setSize(Number(e.target.value))}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="logo">{t("form.uploadLogo")}</Label>
                <Input
                  id="logo"
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="cursor-pointer"
                  placeholder={t("form.placeholder.logo")}
                />
                {logoImage && (
                  <Button onClick={clearLogo} variant="outline" className="mt-2">
                    {t("form.clearLogo")}
                  </Button>
                )}
              </div>

              <Accordion type="single" collapsible>
                <AccordionItem value="advanced">
                  <AccordionTrigger className="text-purple-600">
                    {t("form.advancedConfiguration")}
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 px-1">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>{t("form.advanced.backgroundColor")}</Label>
                          <Input
                            type="color"
                            value={backgroundColor}
                            onChange={(e) => setBackgroundColor(e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>{t("form.advanced.dotColor")}</Label>
                          <Input
                            type="color"
                            value={dotColor}
                            onChange={(e) => setDotColor(e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>{t("form.advanced.cornerSquareColor")}</Label>
                          <Input
                            type="color"
                            value={cornerSquareColor}
                            onChange={(e) => setCornerSquareColor(e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>{t("form.advanced.cornerDotColor")}</Label>
                          <Input
                            type="color"
                            value={cornerDotColor}
                            onChange={(e) => setCornerDotColor(e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>{t("form.advanced.dotShape")}</Label>
                          <Select value={dotShape} onValueChange={setDotShape}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="square">Square</SelectItem>
                              <SelectItem value="dots">Dots</SelectItem>
                              <SelectItem value="rounded">Rounded</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>{t("form.advanced.cornerSquareShape")}</Label>
                          <Select value={cornerSquareShape} onValueChange={setCornerSquareShape}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="square">Square</SelectItem>
                              <SelectItem value="extra-rounded">Extra Rounded</SelectItem>
                              <SelectItem value="dot">Dot</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div>
                        <Label>{t("form.advanced.cornerDotShape")}</Label>
                        <Select value={cornerDotShape} onValueChange={setCornerDotShape}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="square">Square</SelectItem>
                            <SelectItem value="dot">Dot</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>{t("form.advanced.margin")}</Label>
                          <Input
                            type="number"
                            value={margin}
                            onChange={(e) => setMargin(Number(e.target.value))}
                          />
                        </div>
                        <div>
                          <Label>{t("form.advanced.imageMargin")}</Label>
                          <Input
                            type="number"
                            value={imageMargin}
                            onChange={(e) => setImageMargin(Number(e.target.value))}
                          />
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-col items-center gap-6">
          <div ref={qrRef} className="bg-white p-0 rounded-lg shadow-lg min-w-200 min-h-200" />

          <div className="space-y-4 w-full">
            <Button className="w-full" onClick={() => downloadQR('png')}>
              {t("form.download")} (PNG)
            </Button>

            <div className="text-center">
              <p className="text-sm text-gray-600 mb-2">{t("form.exportAs")}</p>
              <div className="flex justify-center gap-4">
                <Button variant="outline" onClick={() => downloadQR('jpeg')}>
                  JPEG
                </Button>
                <Button variant="outline" onClick={() => downloadQR('webp')}>
                  WEBP
                </Button>
                <Button variant="outline" onClick={() => downloadQR('svg')}>
                  SVG
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Templates Section */}
      <div className="mt-16">
        <h2 className="text-2xl font-bold text-center mb-8">{t("templates.title")}</h2>
        <p className="text-center text-gray-600 mb-8">{t("templates.description")}</p>
        <Templates applyTemplate={applyTemplate} />
      </div>
    </div>
  )
}